import { forwardRef, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useRouter } from '@medifind/router';
import { Skeleton } from '@medifind/shared-basic-components';
import { ExpandButton } from '@medifind/shared-basic-components';
import { isPrerender } from '@medifind/utils';
import { selectRouteParams, useRouteStore } from '@medifind/zustand';
import styles from './AccordionSection.module.scss';

const AccordionSection = forwardRef(
  (
    {
      expandable,
      openByScroll,
      relatedSection,
      children,
      nestedAccordions,
      isLoading,
      title,
      style = {},
      id,
      className,
      classes = {},
      noWrap,
      onExpansionChange,
      defaultOpen = false,
    },
    ref,
  ) => {
    const [expanded, setExpanded] = useState(isPrerender || defaultOpen);
    const { articleId } = useRouteStore(selectRouteParams);
    const { location } = useRouter();
    useEffect(() => {
      if (location.hash && id === location.hash.slice(1)) {
        setExpanded(true);
        onExpansionChange && onExpansionChange(true);
      }
    }, [location.hash, id]);

    // Close on page change, unless hash has it open
    useEffect(() => {
      if (location.hash && id !== location.hash.slice(1)) {
        setExpanded(false);
        onExpansionChange && onExpansionChange(false);
      }
    }, [articleId]);

    useEffect(() => {
      if (openByScroll) {
        setExpanded(true);
        onExpansionChange && onExpansionChange(true);
      }
    }, [openByScroll]);
    const toggleExpand = () => {
      setExpanded(!expanded);
      onExpansionChange && onExpansionChange(!expanded);
    };
    return (
      <div ref={ref} id={id} className={classNames(styles['section'], classes.container, className)} style={style.container}>
        <div
          className={classNames(styles['section-header'], classes.header, {
            [styles['related-section']]: relatedSection,
            [styles['clickable']]: expandable,
          })}
          style={style.header}
          {...(expandable
            ? {
                onClick: toggleExpand,
                tabIndex: '-1',
              }
            : {})}
        >
          {isLoading ? (
            <Skeleton width={200} />
          ) : (
            <>
              <div className={classNames(styles['section-header-title'], classes.title, { [styles['wrap']]: !noWrap })} style={style.title}>
                {title}
              </div>
              {expandable && <ExpandButton isExpanded={expanded} style={style} onClick={toggleExpand} title={title} />}
            </>
          )}
        </div>
        <>
          {expanded || !expandable
            ? (Array.isArray(children) ? children.length > 0 : !!children) && (
                <div className={classNames(styles['section-body'], classes.body)} style={style.body}>
                  {children}
                </div>
              )
            : null}
          <div className={classes.nesting} style={style.nesting}>
            {nestedAccordions}
          </div>
        </>
      </div>
    );
  },
);
AccordionSection.displayName - 'AccordionSection';

export { AccordionSection as default, AccordionSection };
